import { Reading } from '../types';

export function generateReadingSummary(reading: Reading): string {
  if (!reading) return 'Unknown Reading';

  // Use reading title if available
  if (reading.readingTitle) {
    return reading.readingTitle;
  }

  // Extract key insights from the AI interpretation
  if (reading.aiInterpretation) {
    const paragraphs = reading.aiInterpretation.split('\n\n');
    
    // Look for the most meaningful sentence that contains key insight phrases
    const insightPhrases = ['suggests', 'indicates', 'points to', 'reveals', 'shows'];
    
    for (const paragraph of paragraphs) {
      const sentences = paragraph.split('. ');
      for (const sentence of sentences) {
        if (insightPhrases.some(phrase => sentence.toLowerCase().includes(phrase))) {
          // Clean up and format the insight
          const insight = sentence
            .replace(/^I sense that |^The cards |^This spread |^This reading /i, '')
            .split(', suggesting')[0]
            .split(', indicating')[0]
            .split(', pointing')[0]
            .trim();
          
          if (insight.length > 10 && insight.length < 120) {
            return insight;
          }
        }
      }
    }
  }
  
  // Fallback to context if available
  if (reading.context) {
    const contextSummary = reading.context.split('.')[0].trim();
    if (contextSummary.length > 10 && contextSummary.length < 120) {
      return contextSummary;
    }
  }
  
  // Final fallback to a theme-based summary
  const mainTheme = reading.themes?.[0]?.name;
  if (mainTheme) {
    const themeDescriptions: Record<string, string> = {
      Transformation: 'A period of significant change and growth',
      Balance: 'Finding harmony and equilibrium in life',
      'Inner Wisdom': 'Connecting with inner guidance and intuition',
      'New Beginnings': 'Starting a fresh chapter in life',
      Healing: 'A journey of restoration and renewal',
      Clarity: 'Gaining clear understanding and insight',
      Abundance: 'Experiencing growth and fulfillment',
      Intuition: 'Following your inner knowing',
      Protection: 'Setting healthy boundaries and finding safety',
      Transition: 'Moving through important life changes'
    };
    
    return themeDescriptions[mainTheme] || `Exploring ${mainTheme.toLowerCase()} in your life`;
  }
  
  return 'Seeking guidance and insight';
}

// Add this function to normalize card names
function normalizeCardName(input: string): string {
  if (!input) return '';
  
  // Convert to lowercase and trim
  let normalized = input.toLowerCase().trim();
  
  // Handle number words and digits
  const numberMap: { [key: string]: string } = {
    'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5',
    'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10',
    'first': '1', 'second': '2', 'third': '3', 'fourth': '4', 'fifth': '5',
    'sixth': '6', 'seventh': '7', 'eighth': '8', 'ninth': '9', 'tenth': '10'
  };

  // Replace number words with digits
  Object.entries(numberMap).forEach(([word, digit]) => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    normalized = normalized.replace(regex, digit);
  });

  // Handle common variations
  const variations: { [key: string]: string } = {
    'judgement': 'judgment',
    'wheel of fortune': 'wheel-of-fortune',
    'high priestess': 'the-high-priestess',
    'hanged man': 'the-hanged-man',
    'the judgement': 'judgment',
    'ace': '1',
    'king': '14',
    'queen': '13',
    'knight': '12',
    'page': '11'
  };

  // Replace variations
  Object.entries(variations).forEach(([variant, standard]) => {
    if (normalized === variant) {
      normalized = standard;
    }
  });

  // Handle "of" in card names (e.g., "2 of cups", "queen of wands")
  const ofMatch = normalized.match(/^(\d+|king|queen|knight|page)\s+of\s+(\w+)$/i);
  if (ofMatch) {
    const [, rank, suit] = ofMatch;
    normalized = `${rank.toLowerCase()}-of-${suit.toLowerCase()}`;
  }

  return normalized;
}

export function standardizeCardName(input: string): string {
  const normalized = normalizeCardName(input);
  
  // Convert to title case
  const words = normalized.split(/[-\s]+/);
  const titleCase = words.map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');

  // Add "The" prefix for major arcana cards that need it
  const needsThe = [
    'Fool', 'Magician', 'High Priestess', 'Empress', 'Emperor',
    'Hierophant', 'Lovers', 'Chariot', 'Hermit', 'Hanged Man',
    'Devil', 'Tower', 'Star', 'Moon', 'Sun', 'World'
  ];

  if (needsThe.includes(titleCase)) {
    return `The ${titleCase}`;
  }

  return titleCase;
}